import {
  TimePeriod,
  ExpirationTimePeriod,
  UserPermissions,
  OSType,
} from "../analyticsfilter.model";
import {
  ChartDefinitions,
  DisplayChartType,
} from "app/analytics/chartDefinitions.config";

//TODO: Analytics - Split out individual configs into their own files

export class UserAnalyticConfiguration {
  public pkid: number;
  public userid: number;
  public configType: string;
  public config: any; // Generic type for flexibility; parsed JSON objects will be assigned
  public updatedDateTime?: Date;

  //public dashboardConfig: DashboardData;

  constructor(init?: Partial<UserAnalyticConfiguration>) {
    Object.assign(this, init);
  }

  static fromJSON(json: any): UserAnalyticConfiguration {
    return Object.assign(new UserAnalyticConfiguration(), json, {
      config: json.config ? JSON.parse(json.config) : {}, // Parse the config JSON
      updatedDateTime: json.updatedDateTime
        ? new Date(json.updatedDateTime)
        : undefined,

      //dashboardConfig: json.dashboardConfig ? DashboardData.fromJSON(JSON.parse(json.dashboardConfig)) : {},
    });
  }

  toJSON() {
    const serialized = {
      pkid: this.pkid,
      userid: this.userid,
      configType: this.configType,
      config: JSON.stringify(this.config),
    };
    return serialized;
  }
}

export class BaseChartSettings {
  //chart
  public chartType: string | DisplayChartType;
  public responsive: boolean;
  public colorScheme: string;
  //public extendedColorScheme: 'data' | 'dataset' | 'label';
  public backgroundColor: string;
  public padding: {
    left: number;
    right: number;
    top: number;
    bottom: number;
  };

  //dataset
  public showBorder: boolean; //only pie and doughnut charts
  public borderColor: string;

  //chart legend
  public showLegend: boolean;
  public legendPosition: "top" | "left" | "bottom" | "right" | "chartArea";

  //legend and list
  public legendLabels: string[];

  //labels
  public showLabels: boolean;
  public labelFontColor: string;
  public labelFontSize: number;
  public labelAnchorPosition: "center" | "start" | "end";
  public labelAlignment:
    | "center"
    | "start"
    | "end"
    | "right"
    | "bottom"
    | "left"
    | "top";
  public labelOffset: number;
  public displayLabels: string[];

  //list
  public showFilter: boolean;
  public paginate: boolean;
  public pageSize: number;
  public showAllData: boolean;

  public static defaults = {
    //default chartType set per component
    responsive: true,
    colorScheme: "other.default",
    extendedColorScheme: "data",
    chartJSColorScheme: "other.default",
    backgroundCanvasColor: "rgba(255, 255, 255, 0.5)",
    padding: {
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
    },
    showBorder: false,
    borderColor: "#ffffff",
    showLegend: false,
    legendPosition: "top",
    showLabels: false,
    labelFontColor: "#ffffff",
    labelFontSize: 16,
    labelAnchorPosition: "center",
    labelAlignment: "end",
    labelOffset: 0,
    defaultDataFill: "rgba(0, 0, 0, 0.1)",
    showFilter: true,
    paginate: false,
    pageSize: 10,
    showAllData: false,
  };

  //TODO: nest these settings for clarity
  constructor(init?: Partial<BaseChartSettings>) {
    this.chartType = init?.chartType || "";
    this.responsive = init?.responsive || BaseChartSettings.defaults.responsive;
    this.colorScheme =
      init?.colorScheme || BaseChartSettings.defaults.colorScheme;
    //this.extendedColorScheme = (init?.extendedColorScheme || BaseChartSettings.defaults.extendedColorScheme) as 'data' | 'dataset' | 'label';
    this.showLegend = init?.showLegend || BaseChartSettings.defaults.showLegend;
    this.legendPosition = (init?.legendPosition ||
      BaseChartSettings.defaults.legendPosition) as
      | "top"
      | "left"
      | "bottom"
      | "right"
      | "chartArea";
    this.backgroundColor =
      init?.backgroundColor || BaseChartSettings.defaults.backgroundCanvasColor;
    this.showBorder = init?.showBorder || BaseChartSettings.defaults.showBorder;
    this.borderColor =
      init?.borderColor || BaseChartSettings.defaults.borderColor;
    this.showLabels = init?.showLabels || BaseChartSettings.defaults.showLabels;
    this.labelFontColor =
      init?.labelFontColor || BaseChartSettings.defaults.labelFontColor;
    this.labelFontSize =
      init?.labelFontSize || BaseChartSettings.defaults.labelFontSize;
    this.labelAnchorPosition = (init?.labelAnchorPosition ||
      BaseChartSettings.defaults.labelAnchorPosition) as
      | "center"
      | "start"
      | "end";
    this.labelAlignment = (init?.labelAlignment ||
      BaseChartSettings.defaults.labelAlignment) as
      | "center"
      | "start"
      | "end"
      | "right"
      | "bottom"
      | "left"
      | "top";
    this.labelOffset =
      init?.labelOffset || BaseChartSettings.defaults.labelOffset;
    this.displayLabels = init?.displayLabels || [];
    this.legendLabels = init?.legendLabels || [];
    this.padding = init?.padding || BaseChartSettings.defaults.padding;
    this.showFilter = init?.showFilter || BaseChartSettings.defaults.showFilter;
    this.paginate = init?.paginate || BaseChartSettings.defaults.paginate;
    this.pageSize = init?.pageSize || BaseChartSettings.defaults.pageSize;
    this.showAllData =
      init?.showAllData || BaseChartSettings.defaults.showAllData;
  }
}

export class BaseAnalyticsConfig {
  generalSettings: BaseChartSettings;
  groupBy: string[];
  filterBy: string[];

  constructor(
    chartType: string | DisplayChartType = "",
    init?: Partial<BaseAnalyticsConfig>
  ) {
    this.generalSettings = new BaseChartSettings({
      chartType,
      ...init?.generalSettings,
    });
    this.groupBy = init?.groupBy || [];
    this.filterBy = init?.filterBy || [];
  }
}

export class ManualsDownloadedConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;
  public minDownloads: number;

  constructor(init?: Partial<ManualsDownloadedConfig>) {
    super(DisplayChartType.Pie, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;
    this.minDownloads = init?.minDownloads || 1;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.ManualsDownloaded,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class ManualsDownloadedLeastConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;
  public bottom: number;

  constructor(init?: Partial<ManualsDownloadedLeastConfig>) {
    super(DisplayChartType.List, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;
    this.bottom = init?.bottom || 10;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.ManualsDownloadedLeast,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class ManualsDownloadedMostConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;
  public top: number;

  constructor(init?: Partial<ManualsDownloadedMostConfig>) {
    super(DisplayChartType.List, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;
    this.top = init?.top || 10;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.ManualsDownloadedMost,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class ManualsDownloadedNeverConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;

  constructor(init?: Partial<ManualsDownloadedNeverConfig>) {
    super(DisplayChartType.List, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.ManualsDownloadedNever,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class ManualsPrintedConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;
  public customLabelSelection: string[];
  constructor(init?: Partial<ManualsPrintedConfig>) {
    super(DisplayChartType.Bar, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;
    this.customLabelSelection =
      init?.customLabelSelection ||
      ChartDefinitions.ManualsPrinted.customLabels["User Data"];

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.ManualsPrinted,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class ManualsOpenedConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;
  public minOpened: number;
  public top: number;
  public customLabelSelection: { [key: string]: string[] };

  constructor(init?: Partial<ManualsOpenedConfig>) {
    super(DisplayChartType.Bar, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;
    this.minOpened = init?.minOpened || 1;
    this.top = init?.top || 10;
    this.customLabelSelection =
      init?.customLabelSelection || ChartDefinitions.ManualsOpened.customLabels;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.ManualsOpened,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class ManualsOpenedCurrentConfig extends BaseAnalyticsConfig {
  public minOpened: number;
  public top: number;
  public customLabelSelection: { [key: string]: string[] };
  public sessionExpirationPeriod: string | ExpirationTimePeriod; // change to ExpirationTimePeriod?

  constructor(init?: Partial<ManualsOpenedCurrentConfig>) {
    super(DisplayChartType.Bar, init); // Pass default chartType and init object
    this.minOpened = init?.minOpened || 1;
    this.top = init?.top || 100;
    this.sessionExpirationPeriod =
      init?.sessionExpirationPeriod || TimePeriod.OneHour;
    this.customLabelSelection =
      init?.customLabelSelection ||
      ChartDefinitions.ManualsOpenedCurrent.customLabels;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.ManualsOpenedCurrent,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class ManualsCreatedConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;

  constructor(init?: Partial<ManualsCreatedConfig>) {
    super(DisplayChartType.List, init);
    this.period = init?.period || TimePeriod.ThreeMonths;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.ManualsCreated,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class ManualsUpdatedConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;

  constructor(init?: Partial<ManualsUpdatedConfig>) {
    super(DisplayChartType.List, init);
    this.period = init?.period || TimePeriod.ThreeMonths;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.ManualsUpdated,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class ManualsLanguageConfig extends BaseAnalyticsConfig {
  constructor(init?: Partial<ManualsLanguageConfig>) {
    super(DisplayChartType.Pie, init); // Pass default chartType and init object

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.ManualsLanguage,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class AnnotationsSentConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;
  public customLabelSelection: { [key: string]: string[] };

  constructor(init?: Partial<AnnotationsSentConfig>) {
    super(DisplayChartType.Bar, init);
    this.period = init?.period || TimePeriod.ThreeMonths;
    this.customLabelSelection =
      init?.customLabelSelection ||
      ChartDefinitions.AnnotationsSent.customLabels;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.AnnotationsSent,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class PagesViewedMostConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;
  public minViewed: number;
  public top: number;

  constructor(init?: Partial<PagesViewedMostConfig>) {
    super(DisplayChartType.List, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;
    this.minViewed = init?.minViewed || 1;
    this.top = init?.top || 100;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.PagesViewed,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class SearchTermsConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;
  public minUsed: number;
  public top: number;
  public customLabelSelection: { [key: string]: string[] };

  constructor(init?: Partial<SearchTermsConfig>) {
    super(DisplayChartType.List, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;
    this.minUsed = init?.minUsed || 1;
    this.top = init?.top || 100;
    this.customLabelSelection =
      init?.customLabelSelection || ChartDefinitions.SearchTerms.customLabels;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.SearchTerms,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class AppUsageConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;
  public userPermission: number | UserPermissions;

  constructor(init?: Partial<AppUsageConfig>) {
    super(DisplayChartType.Grid, init);
    this.period = init?.period || TimePeriod.OneHour;
    this.userPermission = init?.userPermission || UserPermissions.All;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.AppUsage,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class UsersTotalConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;
  public userPermission: number | UserPermissions;
  public startDate: string | Date;
  public endDate: string | Date;

  constructor(init?: Partial<UsersTotalConfig>) {
    super(DisplayChartType.Line, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;
    this.userPermission = init?.userPermission || UserPermissions.All;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.TotalUsers,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class UserSessionsConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;
  public userPermission: number | UserPermissions;
  public startDate: string | Date;
  public endDate: string | Date;
  public sessionExpirationPeriod: string | ExpirationTimePeriod;

  constructor(init?: Partial<UserSessionsConfig>) {
    super(DisplayChartType.Line, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.OneHour;
    this.userPermission = init?.userPermission || UserPermissions.All;
    this.sessionExpirationPeriod =
      init?.sessionExpirationPeriod || ExpirationTimePeriod.OneHour;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.UserSessions,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class UsersCurrentConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;

  constructor(init?: Partial<UsersCurrentConfig>) {
    super(DisplayChartType.Pie, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.UsersCurrent,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class UsersCreatedConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;

  constructor(init?: Partial<UsersCreatedConfig>) {
    super(DisplayChartType.List, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.UsersCreated,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class UsersCreatedTotalConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;

  constructor(init?: Partial<UsersCreatedTotalConfig>) {
    super(DisplayChartType.Pie, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.UsersCreatedTotal,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class UsersLockedConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;

  constructor(init?: Partial<UsersLockedConfig>) {
    super(DisplayChartType.List, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.UsersLocked,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class UsersLockedTotalConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;

  constructor(init?: Partial<UsersLockedTotalConfig>) {
    super(DisplayChartType.Pie, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.UsersLockedTotal,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class UsersRemovedConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;

  constructor(init?: Partial<UsersRemovedConfig>) {
    super(DisplayChartType.List, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.UsersRemoved,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class UsersRemovedTotalConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;

  constructor(init?: Partial<UsersRemovedTotalConfig>) {
    super(DisplayChartType.Pie, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.UsersRemovedTotal,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class AppLanguageConfig extends BaseAnalyticsConfig {
  constructor(init?: Partial<AppLanguageConfig>) {
    super(DisplayChartType.Pie, init); // Pass default chartType and init object

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.AppLanguage,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class AppVersionConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;
  public osType: string | OSType;
  constructor(init?: Partial<AppVersionConfig>) {
    super(DisplayChartType.Pie, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;
    this.osType = init?.osType || OSType.ALL;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.AppVersion,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class OsTypeConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;

  constructor(init?: Partial<OsTypeConfig>) {
    super(DisplayChartType.Pie, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.OsType,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class OsVersionConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;

  constructor(init?: Partial<OsVersionConfig>) {
    super(DisplayChartType.Pie, init); // Pass default chartType and init object
    this.period = init?.period || TimePeriod.ThreeMonths;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.OsVersion,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class UsersSessionsActiveConfig extends BaseAnalyticsConfig {
  public sessionExpirationPeriod: string | ExpirationTimePeriod; // keep as TimePeriod?

  constructor(init?: Partial<UsersSessionsActiveConfig>) {
    super(DisplayChartType.Pie, init); // Pass default chartType and init object
    this.sessionExpirationPeriod =
      init?.sessionExpirationPeriod || ExpirationTimePeriod.OneHour;

    // Define the non-serializable chartDefinition property
    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.UsersSessionsActive,
      writable: true,
      enumerable: false, // Prevent this property from being serialized
    });
  }
}

export class GlobalSearchTermsConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;
  public minUsed: number;
  public top: number;
  public customLabelSelection: { [key: string]: string[] };

  constructor(init?: Partial<GlobalSearchTermsConfig>) {
    super(DisplayChartType.List, init);
    this.period = init?.period || TimePeriod.ThreeMonths;
    this.minUsed = init?.minUsed || 1;
    this.top = init?.top || 100;
    this.customLabelSelection =
      init?.customLabelSelection ||
      ChartDefinitions.GlobalSearchTerms?.customLabels ||
      {};

    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.GlobalSearchTerms,
      writable: true,
      enumerable: false,
    });
  }
}

export class GlobalSearchTermDocumentsConfig extends BaseAnalyticsConfig {
  public period: string | TimePeriod;
  public minUsed: number;
  public top: number;
  public customLabelSelection: { [key: string]: string[] };

  constructor(init?: Partial<GlobalSearchTermDocumentsConfig>) {
    super(DisplayChartType.List, init);
    this.period = init?.period || TimePeriod.ThreeMonths;
    this.minUsed = init?.minUsed || 1;
    this.top = init?.top || 100;
    this.customLabelSelection =
      init?.customLabelSelection ||
      ChartDefinitions.GlobalSearchTermDocuments?.customLabels ||
      {};

    Object.defineProperty(this, "chartDefinition", {
      value: ChartDefinitions.GlobalSearchTermDocuments,
      writable: true,
      enumerable: false,
    });
  }
}
